import type { MultilegComboType } from '@src/components/MultilegCombo/enums';
import type { PreTradeTabType } from '@src/components/PreTrade/types';
import type { BalancesV2TabProps } from '@src/containers/Blotters/BalancesV2/types';
import type { CreditTabProps } from '@src/containers/Blotters/Credit/types';
import type { HedgePositionStatusTabProps } from '@src/containers/Blotters/HedgePositionStatus/types';
import type { LoanTabProps } from '@src/containers/Blotters/OTC/Loans/Loans';
import type { OTCTabProps } from '@src/containers/Blotters/OTC/OTCDerivatives/OTCDerivatives';
import type { PositionsTabProps } from '@src/containers/Blotters/PositionsV3/types';
import type { PriceBlotterTabProps } from '@src/containers/Blotters/PriceBlotter/types';
import type { ReconMismatchesTabProps } from '@src/containers/Blotters/Reconciliation/ReconMismatches';
import type { TransfersTabProps } from '@src/containers/Blotters/Transfers';
import type { CustomerBalancesTabsProps } from '@src/containers/Dealer/CustomerBalances/types';
import type { CustomerTransactionsTabsProps } from '@src/containers/Dealer/CustomerTransactions/types';
import type { OrderDetailsPath, OrderRouteType } from '@src/containers/Trading/Markets/OrderDetails/types';
import type {
  BlotterTableFilter,
  BlotterTableState,
  Column,
  CustomerOrder,
  CustomerTrade,
  DeviceType,
  IconName,
  LedgerAccountTypeEnum,
  MarketDataCardConfig,
  Order,
  ResolutionEnumNoPrefix,
  TabProps,
  TabsState,
  Trade,
  WatchlistProps,
} from '@talos/kyoko';
import type { AgAnnotation, AgPriceVolumeChartType } from 'ag-charts-enterprise';
import type { GroupviewPanelState, IDockviewPanel, IDockviewPanelProps, SerializedDockview } from 'dockview';
import type { FunctionComponent } from 'react';
import type { CustomerOrdersTableFilter } from '../containers/Blotters/CustomerOrders/useCustomerOrdersFilter';
import type { CustomerTradeFilter } from '../containers/Blotters/CustomerTrades/useCustomerTradeFilter';
import type { EntityMonitoringPanelType, EntityName } from '../containers/Monitoring/Blotters/types';
import { ENTITY_MONITORING_PANEL_TYPES } from '../containers/Monitoring/Blotters/types';
import type { OptionFilter, StrikeFilter } from '../containers/Trading/Markets/Options/Filter/types';
import type { DealBookingType } from './OTC/otcSharedTypes';

export interface DockviewPanelParams extends IDockviewPanel {
  params: PanelConfig;
}

export interface LayoutOption {
  label: string;
  value: string;
  hasWatchlist?: boolean;
  layoutSpec: LayoutSpec;
}

export interface TabGroupSpec {
  type: string;
  label: string;
  icon?: IconName;
}

export interface TabComponentSpec {
  type: string;
  label: string;
  group: TabGroupSpec;
  renderer: (props: IDockviewPanelProps) => JSX.Element;
}

export interface LayoutConstraint {
  ids: string[];
  groupId: string;
  maximumWidth?: number;
  minimumWidth?: number;
  maximumHeight?: number;
  minimumHeight?: number;
  width?: number;
  height?: number;
  hidden?: boolean;
}

export const VALID_SECTIONS = ['trading', 'monitoring'] as const;
export type LayoutSection = (typeof VALID_SECTIONS)[number];
export function isValidLayoutSection(section: string | undefined): section is LayoutSection {
  return section != null && VALID_SECTIONS.includes(section as LayoutSection);
}

export interface SpacesSectionConfig {
  initialized: boolean;
  /** Dockview layout specs for this section; canonical store (replaces top-level appConfig `layoutConfigs[section]`). */
  layouts?: LayoutSpec[];
}

/** Persisted `spacesConfigs` root: layout sections plus optional migration metadata. */
export type AppSpacesConfigsPersistence = Record<LayoutSection, SpacesSectionConfig> & {
  /** Set by migrateSpacesNavFromLayoutConfigs; bump the spaces-nav migration version constant to re-run from preserved `layoutConfigs`. */
  spacesNavLayoutMigrationVersion?: number;
};

/** Tab label in the layout add-panel menu for panels with `group: undefined` (one tab per section when both appear). */
export const LAYOUT_SECTION_DISPLAY_LABEL: Record<LayoutSection, string> = {
  trading: 'Trading',
  monitoring: 'Monitoring',
};

export interface LayoutConstraints {
  groupId: string;
  panelIds: string[];
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
}

export interface CollapsablePanelConfig {
  groupId: string;
  direction: 'left' | 'right';
  defaultSize?: number;
  buttonLabel?: string;
}

export type PanelConfig =
  | AccountLedgerEventsDetailsPanelConfig
  | BalancesBlotterPanelConfig
  | CareOrderManagementPanelConfig
  | CareOrdersPanelConfig
  | CreditBlotterPanelConfig
  | CustomerBalancesBlotterPanelConfig
  | CustomerOrdersBlotterPanelConfig
  | CustomerTradesBlotterPanelConfig
  | CustomerTransfersBlotterPanelConfig
  | DeepDivePanelConfig
  | HedgePositionStatusPanelConfig
  | LoansBlotterPanelConfig
  | MarketCardPanelConfig
  | MultilegPanelConfig
  | OTCDerivativesBlotterPanelConfig
  | OptionsPanelConfig
  | OrderDetailsPanelConfig
  | OrdersBlotterPanelConfig
  | PositionAutoHedgingRulesPanelConfig
  | PositionsBlotterPanelConfig
  | PriceGridPanelConfig
  | ReconMismatchesPanelConfig
  | ReconMismatchesDetailsPanelConfig
  | RFQsBlotterPanelConfig
  | TradesBlotterPanelConfig
  | TransfersBlotterPanelConfig
  | WatchlistPanelConfig
  | ManualRFQsPanelConfig
  | HistoricalPriceChartPanelConfig
  | OrderBookPanelConfig
  | MultilegOrderBookPanelConfig
  | TradesHistoryPanelConfig
  | VolumeLadderPanelConfig
  | PriceLadderPanelConfig
  | DealProductBuilderPanelConfig
  | TakerQuoteBreakdownPanelConfig
  | ManualRFQPricingPanelConfig
  | PreTradePanelConfig
  | MonitoringBlotterPanelConfig
  | EntityOverrideMonitoringPanelConfig
  | DefaultPanelConfig;

export interface DefaultPanelConfig extends BasePanelConfig {
  type:
    | 'ordersBlotter'
    | 'rfqsBlotter'
    | 'tradesBlotter'
    | 'customerOrdersBlotter'
    | 'customerTradesBlotter'
    | 'balancesBlotter'
    | 'customerBalancesBlotter'
    | 'positionsBlotter'
    | 'creditBlotter'
    | 'customerCreditBlotter'
    | 'transfersBlotter'
    | 'customerTransfersBlotter'
    | 'marketAccountsBlotter'
    | 'reconMismatches'
    | 'allocations'
    | 'hedgePositionStatus'
    | 'otcDerivatives'
    | 'loans'
    | 'marketCapEstimated';
}

export type DefaultRoutedPanelTypes =
  | 'careOrderManagement'
  | 'careOrders'
  | 'marketCards'
  | 'preTrade'
  | 'priceGrid'
  | 'watchlist'
  | 'manualRfqs'
  | 'takerQuoteBreakdown'
  | 'manualRFQPricing'
  | 'orderBook'
  | 'multilegOrderBook'
  | 'tradesHistory'
  | 'historicalPriceChart'
  | 'priceLadder'
  | 'volumeLadder'
  | 'monitoringBlotter'
  | EntityMonitoringPanelType
  | DefaultPanelConfig['type'];

export interface CareOrdersPanelConfig extends BasePanelConfig {
  type: 'careOrders';
  tabs: TabsState<TabProps & { showAllDecimals?: boolean; expandAllGroups?: boolean }> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}

export interface CareOrderManagementPanelConfig extends BasePanelConfig {
  type: 'careOrderManagement';
  tabs: TabsState<TabProps> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}
export interface MarketCardTabDefaults {
  defaultSubAccountId: number | undefined;
}

export interface ManualRFQsPanelConfig extends BasePanelConfig {
  type: 'manualRfqs';
  showAllDecimals: boolean | undefined;
}

export interface ManualRFQPricingPanelConfig extends BasePanelConfig {
  type: 'manualRFQPricing';
  rfqID: string | undefined;
}

export interface TakerQuoteBreakdownPanelConfig extends BasePanelConfig {
  type: 'takerQuoteBreakdown';
  rfqID: string | undefined;
}

export interface PositionAutoHedgingRulesPanelConfig extends BasePanelConfig {
  type: 'positionAutoHedgingRules';
}

export interface HistoricalPriceChartPanelConfig extends SingleSymbolPanelConfig {
  type: 'historicalPriceChart';
  chartResolution: ResolutionEnumNoPrefix | undefined;
  annotations: AgAnnotation[] | undefined;
  chartType: AgPriceVolumeChartType | undefined;
}

export interface OrderBookPanelConfig extends SingleSymbolPanelConfig {
  type: 'orderBook';
  showCumQty: boolean | undefined;
  priceIncrement: string | undefined;
}

export interface MultilegOrderBookPanelConfig extends BasePanelConfig {
  type: 'multilegOrderBook';
  symbol: string | undefined;
  compactOrderBook: boolean | undefined;
}

export interface VolumeLadderPanelConfig extends SingleSymbolPanelConfig {
  type: 'volumeLadder';
  sizeBuckets: string[];
  priceIncrement: string | undefined;
  resolution: ResolutionEnumNoPrefix;
}

export interface PriceLadderPanelConfig extends SingleSymbolPanelConfig {
  type: 'priceLadder';
  priceIncrement: string | undefined;
}

export interface TradesHistoryPanelConfig extends SingleSymbolPanelConfig {
  type: 'tradesHistory';
  showCumQty: boolean | undefined;
  priceIncrement: string | undefined;
}

export interface WatchlistPanelConfig
  extends
    Omit<
      WatchlistProps,
      | 'enabledColumns'
      | 'securityTypeOptions'
      | 'setShowOnlyFavorites'
      | 'setSecurityTypes'
      | 'setFilteredCurrencies'
      | 'setFavoriteSecurities'
      | 'favoriteSecurities'
    >,
    BasePanelConfig {
  type: 'watchlist';
}

export interface MarketCardPanelConfig extends BasePanelConfig {
  type: 'marketCards';
  columns: MarketDataCardConfig[][];
  sortCards: boolean;
  forceCompact?: boolean;
  defaults?: Partial<MarketCardTabDefaults>;
}

export interface OptionsPanelConfig extends BasePanelConfig {
  type: 'options';
  optionFilter: OptionFilter;
  strikeFilter?: StrikeFilter;
  columnDefsVisibility?: Record<string, boolean>;
}

export interface DeepDivePanelConfig extends BasePanelConfig {
  type: 'deepDive';
  symbol: string;
  aggregation?: string;
  markets: string[];
  marketAccounts: string[];
  priceIncrement?: string;
  showMarkets?: boolean;
  showCumQty?: boolean;
  chartResolution: ResolutionEnumNoPrefix | undefined;
  annotations: AgAnnotation[] | undefined;
  chartType: AgPriceVolumeChartType | undefined;
}

export interface BlotterTablePanelConfig extends BasePanelConfig {
  // filter: Partial<BlotterTableOrderFilter>;
  // columns?: (keyof Order | Partial<Column>)[];
  tabs: TabsState<TabProps> | undefined;
  blotterTablesState?: Record<string, BlotterTableState>;
}

export interface OrderTabProps extends TabProps {
  defaultFilter?: BlotterTableFilter;
  defaultColumns?: (keyof Order | Partial<Column>)[];
}

export interface OrdersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'ordersBlotter';
  tabs: TabsState<OrderTabProps> | undefined;
}

export interface TradeTabProps extends TabProps {
  defaultFilter?: BlotterTableFilter;
  defaultColumns?: (keyof Trade | Partial<Column>)[];
  showAllDecimals?: boolean;
}

export interface TradesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'tradesBlotter';
  tabs: TabsState<TradeTabProps> | undefined;
}

export interface CustomerOrdersTabProps extends TabProps {
  defaultFilter: CustomerOrdersTableFilter;
  defaultColumns?: (keyof CustomerOrder | Partial<Column>)[];
}

export interface CustomerOrdersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerOrdersBlotter';
  tabs: TabsState<CustomerOrdersTabProps> | undefined;
}

export interface CustomerTradesTabProps extends TabProps {
  defaultFilter: CustomerTradeFilter;
  defaultColumns?: (keyof CustomerTrade | Partial<Column>)[];
}

export interface CustomerTradesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerTradesBlotter';
  tabs: TabsState<CustomerTradesTabProps> | undefined;
}

export interface BalancesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'balancesBlotter';
  tabs: TabsState<BalancesV2TabProps> | undefined;
}

export interface CustomerBalancesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerBalancesBlotter';
  tabs: TabsState<CustomerBalancesTabsProps> | undefined;
}

export interface PositionsBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'positionsBlotter';
  tabs: TabsState<PositionsTabProps> | undefined;
}

export interface RFQsBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'rfqsBlotter';
}

export interface CreditBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'creditBlotter';
  tabs: TabsState<CreditTabProps> | undefined;
}

export interface TransfersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'transfersBlotter';
  tabs: TabsState<TransfersTabProps> | undefined;
}

export interface CustomerTransfersBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'customerTransfersBlotter';
  tabs: TabsState<CustomerTransactionsTabsProps> | undefined;
}

export interface OTCDerivativesBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'otcDerivatives';
  tabs: TabsState<OTCTabProps> | undefined;
}

export interface LoansBlotterPanelConfig extends BlotterTablePanelConfig {
  type: 'loans';
  tabs: TabsState<LoanTabProps> | undefined;
}

export interface ReconMismatchesPanelConfig extends BlotterTablePanelConfig {
  type: 'reconMismatches';
  tabs: TabsState<ReconMismatchesTabProps> | undefined;
}

export interface HedgePositionStatusPanelConfig extends BlotterTablePanelConfig {
  type: 'hedgePositionStatus';
  tabs: TabsState<HedgePositionStatusTabProps> | undefined;
}

export interface AccountLedgerEventsDetailsPanelConfig extends BasePanelConfig {
  type: 'accountLedgerEvents';
  accountType: LedgerAccountTypeEnum;
  account: string;
  asset?: string;
}

export interface ReconMismatchesDetailsPanelConfig extends BasePanelConfig {
  type: 'reconMismatchesDetails';
  mismatchIDs: string[];
}

export interface OrderDetailsPanelConfig extends BasePanelConfig {
  type: 'orderDetails';
  orderID: string;
  openTab: OrderDetailsPath;
  orderType: OrderRouteType;
}

export interface MultilegPanelConfig extends BasePanelConfig {
  type: 'multilegDeepDive';
  instrumentType: MultilegComboType;
  resolvedSymbol: string | undefined;
  initiatingLegs?: boolean[];
  haircutRatios?: string[];
  compactOrderBook?: boolean;
  legSymbols?: string[];
  legMarketAccounts?: string[];
  legMarkets?: string[];
}
export interface PriceGridPanelConfig
  extends BasePanelConfig, Partial<Pick<PriceBlotterTabProps, 'defaultColumns' | 'defaultFilter'>> {
  type: 'priceGrid';
}

export interface MonitoringBlotterPanelConfig extends BasePanelConfig {
  type: 'monitoringBlotter';
  entity: EntityName | undefined;
}

export interface EntityOverrideMonitoringPanelConfig extends BasePanelConfig {
  type: EntityMonitoringPanelType;
}

export interface BasePanelConfig {
  type: PanelType;
  icon?: string;
  // Timestamp set when triggering an update to the panel
  refresh?: number;
  customTitle?: boolean;
  closable?: boolean;
  editable?: boolean;
}

export interface SingleSymbolPanelConfig extends BasePanelConfig {
  symbol: string | undefined;
  markets: string[] | undefined;
  marketAccounts: string[] | undefined;
  aggregation: string | undefined;
  enableUnifiedLiquidity: boolean | undefined;
  settingsInitialOpen?: boolean; // Whether to open the settings panel by default
  orderPresetID?: string;
  subAccount?: string;
}

export interface LayoutSpec {
  id: string;
  /** The id of the template used to create the layout */
  templateId?: string;
  /** Whether the layout can be removed from the section, undefined means it can be removed */
  isRemovable?: boolean;
  /** The device type the layout is optimized for, undefined is legacy behavior means it is optimized for all device types */
  deviceType?: DeviceType;
  label: string;
  dockViewLayout: SerializedDockview;
  constraints: LayoutConstraints[];
  collapsablePanels: CollapsablePanelConfig[];
  /** Sidebar space group label in app config; omitted from shared templates (`layoutSpecForSharing`). */
  group?: string;
}

export interface TypedGroupviewPanelState
  extends
    Omit<GroupviewPanelState, 'contentComponent'>,
    Record<keyof Pick<GroupviewPanelState, 'contentComponent'>, PanelType> {}

export interface TypedGroupviewPanelConfigState extends TypedGroupviewPanelState {
  params: PanelConfig;
}

export const PANEL_TYPES = [
  'allocations',
  'marketCards',
  'options',
  'priceGrid',
  'deepDive',
  'orderDetails',
  'watchlist',
  'ordersBlotter',
  'rfqsBlotter',
  'tradesBlotter',
  'customerOrdersBlotter',
  'customerTradesBlotter',
  'balancesBlotter',
  'customerBalancesBlotter',
  'positionsBlotter',
  'creditBlotter',
  'customerCreditBlotter',
  'transfersBlotter',
  'customerTransfersBlotter',
  'marketAccountsBlotter',
  'hedgePositionStatus',
  'positionAutoHedgingRules',
  'reconMismatches',
  'reconMismatchesDetails',
  'multilegDeepDive',
  'accountLedgerEvents',
  'multilegPriceGrid',
  'careOrderManagement',
  'careOrders',
  'manualRfqs',
  'historicalPriceChart',
  'orderBook',
  'tradesHistory',
  'multilegOrderBook',
  'volumeLadder',
  'priceLadder',
  'otcDerivatives',
  'loans',
  'dealProductBuilder',
  'takerQuoteBreakdown',
  'manualRFQPricing',
  'preTrade',
  'monitoringBlotter',
  'marketCapEstimated',
  ...ENTITY_MONITORING_PANEL_TYPES,
] as const;
export type PanelType = (typeof PANEL_TYPES)[number];

export interface PanelComponentSpec {
  type: PanelType;
  /** The main product section the panel belongs to */
  section: LayoutSection;
  label: string;
  /** Used for grouping panels in the add panel menu. Undefined means the panel is not grouped. */
  group: TabGroupSpec | undefined;
  renderer?: FunctionComponent<IDockviewPanelProps>;
  canBeAdded?: boolean;
  icon: IconName | undefined;
  initialWidth?: number;
  initialHeight?: number;
  minWidth?: number;
  minHeight?: number;
}

export interface DealProductBuilderPanelConfig extends BasePanelConfig {
  type: 'dealProductBuilder';
  bookingType: DealBookingType;
  resolvedSymbol?: string;
}

export interface PreTradePanelConfig extends BasePanelConfig {
  type: 'preTrade';
  sessionId: string;
  openTab?: PreTradeTabType;
  requestId?: string;
}
